import { useEffect, useState } from 'react';
import { Button, Checkbox, Label, toast } from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabProjectDetail, LabRelease } from '@/lib/types';

export function RemixSettings({
  release,
  editable,
  onChange,
}: {
  release: LabRelease;
  editable: boolean;
  onChange: (detail: LabProjectDetail) => void;
}) {
  const labs = useLabs();
  const source = release.sourcePackage;
  const sourceReady = source?.status === 'passed';
  const unknownEngine = source?.engine === 'unknown';
  const [allowRemix, setAllowRemix] = useState(release.remixPermission === 'organization');
  const [assetsShared, setAssetsShared] = useState(Boolean(release.assetSharingConfirmedAt));
  const [structureConfirmed, setStructureConfirmed] = useState(Boolean(source?.structureConfirmedAt));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAllowRemix(release.remixPermission === 'organization');
    setAssetsShared(Boolean(release.assetSharingConfirmedAt));
    setStructureConfirmed(Boolean(release.sourcePackage?.structureConfirmedAt));
  }, [release.assetSharingConfirmedAt, release.id, release.remixPermission, release.sourcePackage?.structureConfirmedAt]);

  const savedAllow = release.remixPermission === 'organization';
  const savedAssets = Boolean(release.assetSharingConfirmedAt);
  const savedStructure = Boolean(source?.structureConfirmedAt);
  const dirty = allowRemix !== savedAllow || assetsShared !== savedAssets || (unknownEngine && structureConfirmed !== savedStructure);
  const canAllow = labs.remixingEnabled && sourceReady && assetsShared && (!unknownEngine || structureConfirmed);

  const save = async () => {
    setSaving(true);
    try {
      const detail = await labsApi.updateRelease(release.id, {
        assetSharingConfirmed: assetsShared,
        ...(unknownEngine && structureConfirmed && !savedStructure ? { confirmSourceStructure: true } : {}),
        remixPermission: allowRemix ? 'organization' : 'none',
      });
      onChange(detail);
      toast.success('Saved');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold">Remixing</h2>
      {!labs.remixingEnabled && (
        <p className="text-sm text-muted-foreground">Your university has not turned remixing on.</p>
      )}
      {labs.remixingEnabled && !sourceReady && (
        <p className="text-sm text-muted-foreground">A source package has to pass the security check first.</p>
      )}
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <Checkbox
            id="asset-sharing"
            checked={assetsShared}
            onCheckedChange={(value) => {
              const next = value === true;
              setAssetsShared(next);
              if (!next) setAllowRemix(false);
            }}
            disabled={!editable || saving}
          />
          <Label htmlFor="asset-sharing" className="font-normal leading-5">
            Assets can be shared with other people at this university
          </Label>
        </div>
        {unknownEngine && (
          <div className="flex items-start gap-3">
            <Checkbox
              id="source-structure"
              checked={structureConfirmed}
              onCheckedChange={(value) => setStructureConfirmed(value === true)}
              disabled={!editable || saving || savedStructure}
            />
            <Label htmlFor="source-structure" className="font-normal leading-5">
              This package is one project and contains the files needed to open it
            </Label>
          </div>
        )}
        <div className="flex items-start gap-3">
          <Checkbox
            id="allow-remix"
            checked={allowRemix}
            onCheckedChange={(value) => setAllowRemix(value === true)}
            disabled={!editable || saving || (!canAllow && !allowRemix)}
          />
          <Label htmlFor="allow-remix" className="font-normal leading-5">
            Allow remixing inside this university
            {release.internalRemixTermsVersion ? ` · terms version ${release.internalRemixTermsVersion}` : ''}
          </Label>
        </div>
      </div>
      {editable && (
        <div className="flex justify-end">
          <Button type="button" disabled={!dirty || saving || (allowRemix && !canAllow)} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save remixing'}
          </Button>
        </div>
      )}
    </section>
  );
}
